import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverPortal,
} from "@/components/ui/popover";
import { AnnouncementItem } from "./announcement-item";
import { AnnouncementsTabs } from "./announcement-tabs";
import { AnnouncementHeader } from "./announcement-header";
import { AnnouncementFooter } from "./announcement-footer";
import { useState, type ReactNode, useRef, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import type { ISingleAnnouncement } from "@/types/announcement.interface";
import { useGetAnnouncementInfiniteQuery } from "@/services/api.announcements";
import { AnnouncementMegaPhoneIconSvg } from "@/assets/svgs";

export const AnnouncementMegaPhone = () => {
  const { t } = useTranslation(["common", "nav"]);
  const { unreadAnnouncementCount } = useAppSelector(selectUser);
  const [wave, setWave] = useState(false);
  const prevCountRef = useRef(unreadAnnouncementCount);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (unreadAnnouncementCount > prevCountRef.current) {
      setWave(true);
      timer = setTimeout(() => setWave(false), 600);
    }

    prevCountRef.current = unreadAnnouncementCount;

    return () => timer && clearTimeout(timer);
  }, [unreadAnnouncementCount]);

  return (
    <AnnouncementList>
      <div className="relative group flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-sxs hover:bg-primary/10">
        {/* 🔊 Voice waves */}
        {wave && (
          <>
            <span className="sound-wave sound-wave-sm" />
            <span className="sound-wave sound-wave-md" />
            <span className="sound-wave sound-wave-lg" />
          </>
        )}

        {/* Badge */}
        {unreadAnnouncementCount > 0 && (
          <>
            <span className="absolute -top-1 -right-1 h-3 w-3 animate-ping rounded-full bg-yellow-600 opacity-90" />
            <div className="absolute -top-2 -right-1.5 z-10 flex min-h-4 aspect-square items-center justify-center rounded-full bg-red-500 p-1 text-[9px] font-bold text-white">
              {unreadAnnouncementCount > 99 ? "99+" : unreadAnnouncementCount}
            </div>
          </>
        )}

        <img
          src="/icons/interface-icons/shared/icon-3d-ai-megaphone.png"
          alt={t("nav:modules.announcements")}
          draggable={false}
          className="size-6 object-contain origin-top"
        />
      </div>
    </AnnouncementList>
  );
};

const AnnouncementList = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation(["common", "nav"]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL");

  const {
    data: announcement,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
  } = useGetAnnouncementInfiniteQuery(
    { limit: 5, platformSectionType: activeTab },
    { skip: !open },
  );

  // Flatten pages
  const allAnnouncements =
    announcement?.pages.flatMap((page) => page.data.announcementData) || [];

  const unreadCount = announcement?.pages[0]?.data?.unreadCount || 0;
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (!scrollRef.current || isFetching || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // If scrolled near the bottom, fetch next page
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      fetchNextPage();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="relative w-[340px] sm:w-[380px] rounded-xl p-0 me-2 shadow-lg border-border/60 overflow-hidden"
          align="center"
        >
          <div className="flex h-full flex-col">
            <AnnouncementHeader
              getAllAnnouncementRefetch={refetch}
              unreadCount={unreadCount}
            />
            <AnnouncementsTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              unreadCount={unreadCount}
            />
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="max-h-[380px] min-h-[380px] overflow-y-auto divide-y divide-border/50 scrollbar-thin"
            >
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 px-4 py-3.5"
                  >
                    <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5">
                      <Skeleton className="h-3 w-full rounded" />
                      <Skeleton className="h-2.5 w-2/3 rounded" />
                    </div>
                  </div>
                ))}
              {/* Empty state */}
              {!isLoading &&
                (!allAnnouncements || allAnnouncements.length === 0) && (
                  <div className="flex flex-col items-center justify-center gap-3 min-h-[380px] text-center px-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <AnnouncementMegaPhoneIconSvg className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {t("common:inbox.no_announcements")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t("common:inbox.nothing_to_show_here")}
                      </p>
                    </div>
                  </div>
                )}
              {/* Notifications list */}
              {!isLoading &&
                allAnnouncements &&
                allAnnouncements.length > 0 &&
                allAnnouncements.map((item: ISingleAnnouncement) => (
                  <AnnouncementItem
                    key={item.id}
                    setOpen={setOpen}
                    announcement={item}
                    getAllAnnouncementRefetch={refetch}
                  />
                ))}
              {/* Infinite loading */}
              {isFetching &&
                !isLoading &&
                Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 px-4 py-3.5"
                  >
                    <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
                    <div className="flex-1 space-y-2 pt-0.5">
                      <Skeleton className="h-3 w-full rounded" />
                      <Skeleton className="h-2.5 w-2/3 rounded" />
                    </div>
                  </div>
                ))}
            </div>
            <AnnouncementFooter setOpen={setOpen} />
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
